import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import './SearchableSelect.css';

const OWNER_BADGE_PREF_KEY = 'aot_admin_show_owners_v1';

const readShowOwnersPref = () => {
  try {
    const raw = localStorage.getItem(OWNER_BADGE_PREF_KEY);
    if (raw === null) return true;
    return raw === '1';
  } catch (_) {
    return true;
  }
};

const SearchableSelect = ({
  value,
  onChange,
  options,
  placeholder = "Выберите...",
  searchPlaceholder = "Поиск...",
  displayKey = "name",
  valueKey = "id",
  disabled = false,
  required = false,
  onAddNew,
  addNewLabel = "Добавить новый",
  emptyMessage = "Ничего не найдено",
  formatOption = null, // Функция для кастомного форматирования опций
  formatSelected = null, // Функция для отображения выбранного значения
  formatHierarchyLabel = null, // Подпись пункта в "липкой" иерархии
  hierarchyEnabled = false, // Включить "липкую" иерархию в dropdown
  getParentValue = null, // Функция получения parent-id из опции
  showValidation = false, // Показывать валидацию (красную подсветку)
  invalid = false, // Показывать красную подсветку для невалидного выбранного значения
  fallbackText = '' // Фолбэк-текст для отображения выбранного значения
}) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [stickyIndex, setStickyIndex] = useState(-1);
  const [showOwners, setShowOwners] = useState(readShowOwnersPref);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionsListRef = useRef(null);
  const optionRefs = useRef([]);
  const listboxIdRef = useRef(`searchable-select-listbox-${Math.random().toString(16).slice(2)}`);

  useEffect(() => {
    const handlePref = () => setShowOwners(readShowOwnersPref());
    window.addEventListener('aot-owner-badge-pref', handlePref);
    window.addEventListener('storage', handlePref);
    return () => {
      window.removeEventListener('aot-owner-badge-pref', handlePref);
      window.removeEventListener('storage', handlePref);
    };
  }, []);

  const getBaseOptionLabel = useCallback((option) => {
    if (!option) return '';
    const rawLabel = formatOption ? formatOption(option) : option[displayKey];
    return String(rawLabel || '');
  }, [formatOption, displayKey]);

  const duplicateLabelKeys = useMemo(() => {
    if (!Array.isArray(options) || options.length === 0) return new Set();
    const counts = new Map();
    for (const option of options) {
      const key = getBaseOptionLabel(option).trim().toLowerCase();
      if (!key) continue;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    const duplicates = new Set();
    for (const [key, count] of counts.entries()) {
      if (count > 1) duplicates.add(key);
    }
    return duplicates;
  }, [options, getBaseOptionLabel]);

  const shouldShowOwnerForOption = (option) => {
    if (!user?.is_staff || !showOwners) return false;
    if (!option || !String(option?.owner_email || '').trim()) return false;
    const key = getBaseOptionLabel(option).trim().toLowerCase();
    if (!key) return false;
    return duplicateLabelKeys.has(key);
  };

  const renderOptionLabel = (option) => {
    const base = getBaseOptionLabel(option);
    if (!shouldShowOwnerForOption(option)) return base;
    return (
      <span className="searchable-select-label-with-owner">
        <span>{base}</span>
        <span className="searchable-select-owner-tag">owner:{option.owner_email}</span>
      </span>
    );
  };

  // Фильтрация опций при изменении поискового запроса
  useEffect(() => {
    if (searchTerm) {
      const filtered = options.filter(option => {
        const displayValue = getBaseOptionLabel(option);
        return displayValue.toLowerCase().includes(searchTerm.toLowerCase());
      });
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options, getBaseOptionLabel]);

  // Закрытие dropdown при клике вне
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Фокус на поле поиска при открытии
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!filteredOptions.length) {
      setHighlightedIndex(-1);
      return;
    }
    const selectedIndex = filteredOptions.findIndex(
      (option) => String(option[valueKey]) === String(value)
    );
    setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }, [isOpen, filteredOptions, value, valueKey]);

  useEffect(() => {
    if (!isOpen || highlightedIndex < 0) return;
    const node = optionRefs.current[highlightedIndex];
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setStickyIndex(-1);
      return;
    }

    const container = optionsListRef.current;
    if (!container) return;

    const updateStickyByScroll = () => {
      const scrollTop = container.scrollTop;
      let nextIndex = -1;
      for (let i = 0; i < filteredOptions.length; i += 1) {
        const node = optionRefs.current[i];
        if (!node) continue;
        const bottom = node.offsetTop + node.offsetHeight;
        if (bottom > scrollTop + 1) {
          nextIndex = i;
          break;
        }
      }
      if (nextIndex === -1 && filteredOptions.length > 0) {
        nextIndex = filteredOptions.length - 1;
      }
      setStickyIndex((prev) => (prev === nextIndex ? prev : nextIndex));
    };

    updateStickyByScroll();
    container.addEventListener('scroll', updateStickyByScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', updateStickyByScroll);
    };
  }, [isOpen, filteredOptions]);

  // Обработка Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape, true);
      return () => {
        document.removeEventListener('keydown', handleEscape, true);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.classList.add('aot-searchable-select-open');
    const handleForcedClose = () => setIsOpen(false);
    document.addEventListener('aot-close-searchable-select', handleForcedClose);
    return () => {
      document.removeEventListener('aot-close-searchable-select', handleForcedClose);
      if (!document.querySelector('.searchable-select.open')) {
        document.body.classList.remove('aot-searchable-select-open');
      }
    };
  }, [isOpen]);

  const handleSelect = (option) => {
    onChange(option[valueKey]);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleAddNew = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
    if (onAddNew) {
      onAddNew();
    }
  };

  const getSelectedDisplay = () => {
    const selected = options.find((option) => String(option[valueKey]) === String(value));
    if (selected) {
      if (formatSelected) {
        return formatSelected(selected);
      }
      if (shouldShowOwnerForOption(selected)) {
        return `${getBaseOptionLabel(selected)} (owner:${selected.owner_email})`;
      }
      return getBaseOptionLabel(selected);
    }
    return fallbackText || '';
  };
  const selectedText = getSelectedDisplay();

  const getOptionLabel = (option) => {
    if (!option) return '';
    return renderOptionLabel(option);
  };

  const getHierarchyOptionLabel = (option) => {
    if (!option) return '';
    if (formatHierarchyLabel) return formatHierarchyLabel(option);
    return getOptionLabel(option);
  };

  const activeHierarchyOption = (() => {
    if (!hierarchyEnabled) return null;
    if (isOpen && stickyIndex >= 0 && filteredOptions[stickyIndex]) {
      return filteredOptions[stickyIndex];
    }
    if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
      return filteredOptions[highlightedIndex];
    }
    return options.find((option) => String(option[valueKey]) === String(value)) || null;
  })();

  const hierarchyTrail = (() => {
    if (!hierarchyEnabled || !getParentValue || !activeHierarchyOption) return [];
    const byId = new Map((options || []).map((option) => [String(option[valueKey]), option]));
    const trail = [];
    const seen = new Set();
    let cursor = activeHierarchyOption;
    while (cursor) {
      const cursorId = String(cursor[valueKey]);
      if (!cursorId || seen.has(cursorId)) break;
      seen.add(cursorId);
      trail.unshift(cursor);
      const parentId = getParentValue(cursor);
      if (parentId === null || parentId === undefined || parentId === '') break;
      cursor = byId.get(String(parentId)) || null;
    }
    return trail;
  })();

  const handleKeyDown = (e) => {
    if (disabled) return;
    const { key } = e;
    const hasOptions = filteredOptions.length > 0;

    if (key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      if (!hasOptions) return;
      setHighlightedIndex((prev) => (prev < 0 ? 0 : Math.min(prev + 1, filteredOptions.length - 1)));
      return;
    }

    if (key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      if (!hasOptions) return;
      setHighlightedIndex((prev) => (prev <= 0 ? 0 : prev - 1));
      return;
    }

    if (key === 'Home' && isOpen && hasOptions) {
      e.preventDefault();
      setHighlightedIndex(0);
      return;
    }

    if (key === 'End' && isOpen && hasOptions) {
      e.preventDefault();
      setHighlightedIndex(filteredOptions.length - 1);
      return;
    }

    if (key === 'Enter') {
      if (!isOpen) {
        e.preventDefault();
        setIsOpen(true);
        return;
      }
      if (hasOptions && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        e.preventDefault();
        e.stopPropagation();
        handleSelect(filteredOptions[highlightedIndex]);
      }
      return;
    }

    if (key === 'Escape' && isOpen) {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
      setSearchTerm('');
      setHighlightedIndex(-1);
      return;
    }
  };

  if (disabled) {
    return (
      <div className="searchable-select disabled">
        <div
          className="searchable-select-display"
          data-tooltip={selectedText || undefined}
          title={selectedText || undefined}
        >
          {selectedText || placeholder}
        </div>
      </div>
    );
  }

  return (
    <div className="searchable-select-container" ref={dropdownRef}>
      <div className="searchable-select-wrapper">
        <div 
          className={`searchable-select ${isOpen ? 'open' : ''} ${required && !value && showValidation ? 'required' : ''} ${invalid ? 'invalid' : ''}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          tabIndex={disabled ? -1 : 0}
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? listboxIdRef.current : undefined}
          aria-haspopup="listbox"
          aria-activedescendant={
            isOpen && highlightedIndex >= 0 ? `${listboxIdRef.current}-option-${highlightedIndex}` : undefined
          }
        >
          <div
            className="searchable-select-display"
            data-tooltip={selectedText || undefined}
            title={selectedText || undefined}
          >
            {selectedText || <span className="placeholder">{placeholder}</span>}
          </div>
          <div className="searchable-select-arrow">▼</div>
        </div>
        
        {onAddNew && (
          <button
            type="button"
            className="btn-add-new"
            onClick={handleAddNew}
            title={addNewLabel}
          >
            +
          </button>
        )}
      </div>

      {isOpen && (
        <div className="searchable-select-dropdown">
          <div className="searchable-select-search">
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={searchPlaceholder}
              className="searchable-select-search-input"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          {hierarchyEnabled && hierarchyTrail.length > 1 && (
            <div className="searchable-select-hierarchy-sticky">
              {hierarchyTrail.slice(0, -1).map((option, idx) => (
                <div key={`${String(option[valueKey])}-${idx}`} className="searchable-select-hierarchy-row">
                  {getHierarchyOptionLabel(option)}
                </div>
              ))}
            </div>
          )}
          
          <div
            className="searchable-select-options"
            id={listboxIdRef.current}
            role="listbox"
            ref={optionsListRef}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  id={`${listboxIdRef.current}-option-${index}`}
                  key={option[valueKey]}
                  ref={(el) => { optionRefs.current[index] = el; }}
                  className={`searchable-select-option ${option[valueKey] === value ? 'selected' : ''} ${filteredOptions[highlightedIndex]?.[valueKey] === option[valueKey] ? 'highlighted' : ''}`}
                  onClick={() => handleSelect(option)}
                  role="option"
                  aria-selected={option[valueKey] === value}
                >
                  {getOptionLabel(option)}
                </div>
              ))
            ) : (
              <div className="searchable-select-empty">
                {emptyMessage}
                {onAddNew && searchTerm && (
                  <button
                    type="button"
                    className="btn-create-from-search"
                    onClick={handleAddNew}
                  >
                    Создать "{searchTerm}"
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
